import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Popover,
  Stack,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';

const InventoryHeaderControls = ({
  itemCount,
  onAddItem,
  selectedChannelId,
  onQuickAdd,
  channelsMap,
  channels = [],
}) => {
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddAnchorEl, setQuickAddAnchorEl] = useState(null);
  const [quickAddData, setQuickAddData] = useState({ itemTitle: '', cost: '', listPrice: '' });
  const [isQuickAdding, setIsQuickAdding] = useState(false);

  const handleQuickAddChange = (e) => {
    const { name, value } = e.target;
    setQuickAddData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuickAddSubmit = async () => {
    if (!selectedChannelId) return;
    if (!quickAddData.itemTitle || !quickAddData.cost || !quickAddData.listPrice) return;

    setIsQuickAdding(true);
    const channel = channels.find((ch) => ch.id === selectedChannelId);
    const created = await onQuickAdd({
      itemTitle: quickAddData.itemTitle,
      cost: quickAddData.cost,
      listPrice: quickAddData.listPrice,
      salesChannelId: selectedChannelId,
      salesLocation: channel?.defaultLocation || '',
    });
    setIsQuickAdding(false);

    if (created) {
      setQuickAddData({ itemTitle: '', cost: '', listPrice: '' });
      setShowQuickAdd(false);
      setQuickAddAnchorEl(null);
    }
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Inventory Items ({itemCount})
          </Typography>
        </Box>

        {/* Center: Add Item Button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddItem}
          sx={{
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          Add Item
        </Button>

        <Button
          variant="contained"
          onClick={(event) => {
            const nextOpen = !showQuickAdd;
            setShowQuickAdd(nextOpen);
            setQuickAddAnchorEl(nextOpen ? event.currentTarget : null);
          }}
          disabled={!selectedChannelId}
          sx={{
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          + Quick add
        </Button>
      </Box>

      <Popover
        open={showQuickAdd}
        anchorEl={quickAddAnchorEl}
        onClose={() => {
          setShowQuickAdd(false);
          setQuickAddAnchorEl(null);
          setQuickAddData({ itemTitle: '', cost: '', listPrice: '' });
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 420 }}>
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Title"
              name="itemTitle"
              value={quickAddData.itemTitle}
              onChange={handleQuickAddChange}
              autoFocus
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Cost ($)"
                name="cost"
                type="number"
                value={quickAddData.cost}
                onChange={handleQuickAddChange}
                inputProps={{ min: '0', step: '0.01' }}
              />
              <TextField
                fullWidth
                size="small"
                label="List Price ($)"
                name="listPrice"
                type="number"
                value={quickAddData.listPrice}
                onChange={handleQuickAddChange}
                inputProps={{ min: '0', step: '0.01' }}
              />
            </Stack>
            <TextField
              size="small"
              label="Channel"
              value={channelsMap[selectedChannelId] || ''}
              disabled
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => {
                  setShowQuickAdd(false);
                  setQuickAddAnchorEl(null);
                  setQuickAddData({ itemTitle: '', cost: '', listPrice: '' });
                }}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleQuickAddSubmit}
                disabled={
                  isQuickAdding ||
                  !quickAddData.itemTitle ||
                  !quickAddData.cost ||
                  !quickAddData.listPrice
                }
                sx={{ textTransform: 'none' }}
              >
                {isQuickAdding ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default React.memo(InventoryHeaderControls);
